import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Settings as SettingsIcon, BellRing, Wand2, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Avatar from "./Avatar";
import NotificationControl from "./NotificationControl";
import AccountSection from "./AccountSection";
import MochiAndYouCard from "./settings/MochiAndYouCard";
import MochiThisMonthCard from "./settings/MochiThisMonthCard";
import NotificationModePicker from "./settings/NotificationModePicker";
import { fetchMochiThisMonth, fetchBypassStatus, setLoginBypass } from "@/lib/api";

// Settings sheet container — section cards live in ./settings/ subcomponents.
export default function SettingsPanel({ open, onClose, settings, onUpdate, isAdmin = false, isBetaAdmin = false, bypassAllowed = false, onUnlockAll }) {
    const [monthly, setMonthly] = React.useState(null);
    const [bypassEnabled, setBypassEnabled] = React.useState(false);
    const [bypassBusy, setBypassBusy] = React.useState(false);

    // Companion name is edited against a LOCAL draft. Persisting per
    // keystroke (the old behavior) raced the async PATCH echo against the
    // controlled input and garbled what the user typed (e.g. "Bobo" →
    // "Mochio"). The draft commits on blur/Enter and via a short debounce.
    const [nameDraft, setNameDraft] = React.useState(settings?.avatar_name ?? "");
    const nameDirtyRef = React.useRef(false);

    // Follow server value only while the user isn't mid-edit.
    React.useEffect(() => {
        if (!nameDirtyRef.current) setNameDraft(settings?.avatar_name ?? "");
    }, [settings?.avatar_name]);

    const commitName = React.useCallback(() => {
        if (!nameDirtyRef.current) return;
        nameDirtyRef.current = false;
        const next = nameDraft.trim();
        if (next && next !== settings?.avatar_name) {
            onUpdate({ avatar_name: next });
        } else {
            setNameDraft(settings?.avatar_name ?? "");
        }
    }, [nameDraft, settings?.avatar_name, onUpdate]);

    // Debounced auto-commit — covers closing the panel without blurring.
    React.useEffect(() => {
        if (!nameDirtyRef.current) return undefined;
        const t = setTimeout(commitName, 800);
        return () => clearTimeout(t);
    }, [nameDraft, commitName]);

    // Refresh the "Mochi this month" stat whenever the panel opens so the count
    // is always fresh. Silently fails — the card just won't render if the
    // backend call errors.
    React.useEffect(() => {
        if (!open) return;
        let cancelled = false;
        fetchMochiThisMonth()
            .then((d) => { if (!cancelled) setMonthly(d); })
            .catch(() => { if (!cancelled) setMonthly(null); });
        return () => { cancelled = true; };
    }, [open]);

    // Fetch the current preview "skip sign-in" state (owner + preview only).
    React.useEffect(() => {
        if (!open || !isAdmin || !bypassAllowed) return;
        let cancelled = false;
        fetchBypassStatus()
            .then((d) => { if (!cancelled) setBypassEnabled(!!d?.enabled); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [open, isAdmin, bypassAllowed]);

    const toggleBypass = async () => {
        if (bypassBusy) return;
        setBypassBusy(true);
        try {
            const res = await setLoginBypass(!bypassEnabled);
            setBypassEnabled(!!res?.enabled);
            toast.success(res?.enabled
                ? "Sign-in skip ON — this preview auto-opens as you 🍡"
                : "Sign-in skip OFF — sign out to see the login page again.");
        } catch {
            toast.error("Couldn't change that — try again?");
        } finally {
            setBypassBusy(false);
        }
    };

    if (!settings) return null;

    // Days known: from first_active_date to today
    const daysKnown = (() => {
        if (!settings.first_active_date) return 0;
        try {
            const d0 = new Date(settings.first_active_date + "T12:00:00");
            const d1 = new Date();
            return Math.max(0, Math.floor((d1 - d0) / 86400000));
        } catch { return 0; }
    })();

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40 bg-warm-900/30 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="settings-overlay"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-cream rounded-t-clay sm:rounded-clay shadow-float p-5 sm:p-6 max-h-[92dvh] overflow-y-auto"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}
                    >
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-2">
                                <SettingsIcon size={20} className="text-terracotta" />
                                <h3 className="font-display text-2xl text-warm-900">Settings</h3>
                            </div>
                            <button onClick={onClose} className="text-warm-500 p-1.5 rounded-full hover:bg-surface" data-testid="settings-close" aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>

                        {/* Account — guest claim / sign in / sign out */}
                        <AccountSection open={open} />

                        {/* Mochi & you — relationship progression */}
                        <MochiAndYouCard settings={settings} onUpdate={onUpdate} daysKnown={daysKnown} isAdmin={isAdmin} onUnlockAll={onUnlockAll} />

                        {/* Beta-access admin — only shown to admin-email accounts. */}
                        {isBetaAdmin && (
                            <a
                                href="/admin"
                                className="mb-5 flex items-center gap-3 rounded-clay bg-terracotta/10 border border-terracotta/30 p-4 transition-all hover:bg-terracotta/15"
                                data-testid="settings-admin-link"
                            >
                                <ShieldCheck size={18} className="text-terracotta shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-display text-sm text-warm-900 leading-tight">Beta Access admin</p>
                                    <p className="font-body text-[11px] text-warm-500 mt-0.5">
                                        Manage invite codes, allowlist & beta users 🍡
                                    </p>
                                </div>
                                <span className="font-display text-terracotta text-lg">→</span>
                            </a>
                        )}

                        {/* Owner tools — preview-only "skip sign-in" toggle. */}
                        {isAdmin && bypassAllowed && (
                            <div className="mb-5 rounded-clay bg-warm-900/5 border border-warm-300/40 p-4" data-testid="owner-tools-card">
                                <p className="font-display text-xs uppercase tracking-widest text-warm-500 mb-2 flex items-center gap-1">
                                    <Wand2 size={12} /> Owner tools · preview
                                </p>
                                <button
                                    onClick={toggleBypass}
                                    disabled={bypassBusy}
                                    className="w-full flex items-center gap-3 rounded-2xl bg-cream/90 px-3 py-2.5 text-left transition-all hover:bg-cream disabled:opacity-60"
                                    data-testid="settings-login-bypass-toggle"
                                    aria-pressed={bypassEnabled}
                                >
                                    <KeyRound size={18} className="text-terracotta shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-display text-sm text-warm-900 leading-tight">Skip sign-in on this preview</p>
                                        <p className="font-body text-[11px] text-warm-500 mt-0.5">
                                            {bypassEnabled
                                                ? "On — this preview auto-opens as you, no login."
                                                : "Off — the login page shows on load."}
                                        </p>
                                    </div>
                                    <span
                                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${bypassEnabled ? "bg-terracotta" : "bg-warm-500/30"}`}
                                        data-testid="settings-login-bypass-switch"
                                    >
                                        <span
                                            className={`inline-block h-5 w-5 transform rounded-full bg-cream shadow-clay transition-transform ${bypassEnabled ? "translate-x-5" : "translate-x-0.5"}`}
                                        />
                                    </span>
                                </button>
                                <p className="font-body text-[11px] text-warm-500 mt-2 italic">
                                    Only works in preview — never in production. The login page is never removed, just skipped here.
                                </p>
                            </div>
                        )}

                        {/* Mochi this month — engagement card */}
                        <MochiThisMonthCard monthly={monthly} />

                        <div className="mb-5">
                            <p className="font-body text-xs uppercase tracking-widest text-warm-500 mb-2">Companion name</p>
                            <input
                                type="text"
                                value={nameDraft}
                                onChange={(e) => {
                                    nameDirtyRef.current = true;
                                    setNameDraft(e.target.value);
                                }}
                                onBlur={commitName}
                                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                maxLength={30}
                                className="w-full bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 outline-none border-2 border-transparent focus:border-terracotta transition-all"
                                data-testid="settings-avatar-name"
                            />
                        </div>

                        <div className="mb-5">
                            <p className="font-body text-xs uppercase tracking-widest text-warm-500 mb-2">Avatar style</p>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { id: "feminine", label: "Feminine", desc: "With blushy cheeks" },
                                    { id: "masculine", label: "Masculine", desc: "No blush" },
                                ].map((opt) => {
                                    const sel = (settings.avatar_style || "feminine") === opt.id;
                                    return (
                                        <button
                                            key={opt.id}
                                            onClick={() => onUpdate({ avatar_style: opt.id })}
                                            className={`p-3 rounded-2xl transition-all border-2 flex flex-col items-center gap-1 ${
                                                sel ? "bg-surface border-terracotta shadow-clay" : "bg-surface/50 border-transparent hover:bg-surface"
                                            }`}
                                            data-testid={`settings-style-${opt.id}`}
                                        >
                                            <Avatar mood="happy" size={72} style={opt.id} />
                                            <p className="font-display text-warm-900 text-sm mt-1">{opt.label}</p>
                                            <p className="font-body text-xs text-warm-500">{opt.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <NotificationControl />

                        <NotificationModePicker
                            value={settings.notification_mode}
                            onChange={(mode) => onUpdate({ notification_mode: mode })}
                        />

                        <p className="font-body text-xs text-warm-500 mt-5 text-center">
                            <BellRing size={12} className="inline-block mr-1" />
                            Browser notifications need your permission once.
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
